/**
 * Distance travelled and cleaning time per task, as the console holds it.
 *
 * WHERE THE NUMBERS COME FROM. The fleet adapter counts wheel-odometer
 * metres per task (gausium-phantas schema 10); the worker projects each
 * task's CUMULATIVE totals onto `rmf/v1/sites/{site}/task_distance/{id}`
 * about every 15 s while it runs, then three finals 5 s apart; the
 * api-server keeps the latest in Postgres. A console sees them three ways:
 *
 *   - live, on the SSE stream every console already has open
 *     (`deriveV1LiveEvent` -> `task_distance` -> `noteLiveTaskDistance`);
 *   - caught up, for the live rows, when the Tasks tab opens
 *     (`useTaskDistanceCatchUp`, `GET .../history/task_distances`);
 *   - on the older view's rows, which carry them on the completion record
 *     (`completionDistance`).
 *
 * ONE ORDERING RULE for all three, the adapter's own: the highest `at_ms`
 * wins; at an equal `at_ms` the totals are equal by contract and `final`
 * is OR-ed, so the three repeated finals change nothing; a LATER running
 * total after a final reopens the task (RMF resumed it, and its totals
 * carry on from the final). A catch-up answer landing after a newer live
 * message therefore cannot drag a row backwards.
 *
 * WHY A STORE OF ITS OWN and not the query cache. The stream writes here
 * on every console whether or not the Tasks tab is open -- one small
 * object per running task per 15 s, cheaper than the JSON parse the
 * transport has already done -- and a query-cache entry with no observer
 * is garbage-collected after five minutes, which would throw away exactly
 * the finals a person opening the tab later needs. The `feed-health.ts`
 * pattern: a module-level map per site, read through
 * `useSyncExternalStore`, a new map only when something changed.
 */
import { useSyncExternalStore } from 'react';
import type { TaskCompletionRecord, TaskDistanceRecord } from './v1';

/** A task's distance totals, whichever way they arrived. */
export interface TaskDistanceView {
  readonly taskId: string;
  /** Wheel distance driven while the task was current, metres. */
  readonly distanceM: number;
  /** The part of it driven while the clean's action was open. */
  readonly cleanDistanceM: number;
  /** The vendor's "clean running" flag, seconds. Includes the undock
   *  and the drive to where the coverage starts. */
  readonly activeCleanS: number;
  /** Head-down cleaning time, seconds -- what "Cleaning time" shows.
   *  Null from an adapter that does not report the cleaning head. */
  readonly brushDownCleanS: number | null;
  readonly discontinuities: number;
  /** Some stretch could not be measured: every number is a LOWER bound. */
  readonly partial: boolean;
  /** The adapter's final totals, rather than its latest running ones. */
  readonly final: boolean;
  /** The adapter's clock at the last reading in these totals. */
  readonly atMs: number;
}

export type TaskDistanceMap = ReadonlyMap<string, TaskDistanceView>;

function finiteNonNegative(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;
}

/**
 * A live `TaskDistance` payload as a view, or null when it is not one.
 *
 * The wire is not trusted: a payload missing a total, carrying a negative
 * one, or naming a different task from its key is dropped whole rather
 * than shown as a guess. `taskId` is the id the KEY named -- the bus ACL
 * authenticates the key, not the payload's fields.
 */
export function adaptTaskDistance(payload: unknown, taskId: string): TaskDistanceView | null {
  if (!payload || typeof payload !== 'object' || !taskId) return null;
  const p = payload as Record<string, unknown>;
  if (p.task_id !== taskId) return null;
  const distanceM = finiteNonNegative(p.distance_m);
  const cleanDistanceM = finiteNonNegative(p.clean_distance_m);
  const activeCleanS = finiteNonNegative(p.active_clean_s);
  const discontinuities = finiteNonNegative(p.discontinuities);
  const atMs = finiteNonNegative(p.at_ms);
  if (
    distanceM === null ||
    cleanDistanceM === null ||
    activeCleanS === null ||
    discontinuities === null ||
    atMs === null ||
    typeof p.final !== 'boolean'
  ) {
    return null;
  }
  const brush = p.brush_down_clean_s;
  const brushDownCleanS = brush === null || brush === undefined ? null : finiteNonNegative(brush);
  if (brush !== null && brush !== undefined && brushDownCleanS === null) return null;
  return {
    taskId,
    distanceM,
    cleanDistanceM,
    activeCleanS,
    brushDownCleanS,
    discontinuities: Math.round(discontinuities),
    partial: discontinuities > 0,
    final: p.final,
    atMs,
  };
}

/** A catch-up record as a view; it carries the same fields under the
 *  same names, with the task id inside `task_id`. */
export function adaptTaskDistanceRecord(r: TaskDistanceRecord): TaskDistanceView | null {
  return adaptTaskDistance(
    {
      task_id: r?.task_id?.task,
      distance_m: r?.distance_m,
      clean_distance_m: r?.clean_distance_m,
      active_clean_s: r?.active_clean_s,
      brush_down_clean_s: r?.brush_down_clean_s,
      discontinuities: r?.discontinuities,
      final: r?.final,
      at_ms: r?.at_ms,
    },
    r?.task_id?.task ?? '',
  );
}

/**
 * An older row's distance, from the completion record's copy of it. Null
 * -- a dash, never "0 m" -- when the record carries no distance: a task
 * that ended before counting began, or an api-server that predates it.
 */
export function completionDistance(r: TaskCompletionRecord): TaskDistanceView | null {
  const distanceM = finiteNonNegative(r.distance_m);
  if (distanceM === null) return null;
  const discontinuities = finiteNonNegative(r.distance_discontinuities) ?? 0;
  return {
    taskId: r.task_id.task,
    distanceM,
    cleanDistanceM: finiteNonNegative(r.clean_distance_m) ?? 0,
    activeCleanS: finiteNonNegative(r.active_clean_s) ?? 0,
    brushDownCleanS: finiteNonNegative(r.brush_down_clean_s),
    discontinuities: Math.round(discontinuities),
    // The server's own flag when it sends one; the rule it derives it by
    // when it does not.
    partial: typeof r.distance_partial === 'boolean' ? r.distance_partial : discontinuities > 0,
    final: r.distance_final !== false,
    // Not sent on the completion record, and not needed: an older row is
    // never merged with anything -- it is read whole, page by page.
    atMs: 0,
  };
}

/**
 * The adapter's ordering rule (see the module header). Returns `prev`
 * ITSELF when `next` adds nothing, so a repeated final re-renders nobody.
 */
export function mergeTaskDistance(
  prev: TaskDistanceView | undefined,
  next: TaskDistanceView,
): TaskDistanceView {
  if (!prev) return next;
  if (next.atMs < prev.atMs) return prev;
  if (next.atMs > prev.atMs) return next;
  // The same reading. Equal totals by contract; the max is belt and
  // braces (the worker does the same), so a producer that broke the
  // contract still cannot make a shown total go down.
  const brush =
    prev.brushDownCleanS === null
      ? next.brushDownCleanS
      : next.brushDownCleanS === null
        ? prev.brushDownCleanS
        : Math.max(prev.brushDownCleanS, next.brushDownCleanS);
  const merged: TaskDistanceView = {
    taskId: prev.taskId,
    distanceM: Math.max(prev.distanceM, next.distanceM),
    cleanDistanceM: Math.max(prev.cleanDistanceM, next.cleanDistanceM),
    activeCleanS: Math.max(prev.activeCleanS, next.activeCleanS),
    brushDownCleanS: brush,
    discontinuities: Math.max(prev.discontinuities, next.discontinuities),
    partial: prev.partial || next.partial,
    final: prev.final || next.final,
    atMs: prev.atMs,
  };
  const same =
    merged.distanceM === prev.distanceM &&
    merged.cleanDistanceM === prev.cleanDistanceM &&
    merged.activeCleanS === prev.activeCleanS &&
    merged.brushDownCleanS === prev.brushDownCleanS &&
    merged.discontinuities === prev.discontinuities &&
    merged.partial === prev.partial &&
    merged.final === prev.final;
  return same ? prev : merged;
}

// ---------------------------------------------------------------------------
// The per-site store
// ---------------------------------------------------------------------------

/**
 * Tasks remembered per site. NoMad ends about sixty a day, so this is
 * months of a console left open; past it the ones with the oldest reading
 * go first. A bound on memory, not on correctness: a task forgotten here
 * shows a dash until the next catch-up or live message names it again.
 */
export const MAX_TASK_DISTANCES = 5000;

const EMPTY: TaskDistanceMap = new Map();
const _bySite = new Map<string, TaskDistanceMap>();
const _listeners = new Set<() => void>();

/** Fold `views` into `site`'s map. Subscribers see a NEW map only when a
 *  task's numbers actually changed. */
export function noteTaskDistances(site: string, views: readonly TaskDistanceView[]): void {
  if (!site || views.length === 0) return;
  const prev = _bySite.get(site) ?? EMPTY;
  let next: Map<string, TaskDistanceView> | null = null;
  for (const view of views) {
    const current = (next ?? prev).get(view.taskId);
    const merged = mergeTaskDistance(current, view);
    if (merged === current) continue;
    next ??= new Map(prev);
    next.set(view.taskId, merged);
  }
  if (!next) return;
  if (next.size > MAX_TASK_DISTANCES) {
    const oldestFirst = [...next.values()].sort((a, b) => a.atMs - b.atMs);
    for (const view of oldestFirst.slice(0, next.size - MAX_TASK_DISTANCES)) {
      next.delete(view.taskId);
    }
  }
  _bySite.set(site, next);
  for (const l of _listeners) l();
}

/** One live `task_distance` event; a payload that fails validation is
 *  dropped without a word -- the row keeps what it had. */
export function noteLiveTaskDistance(site: string, taskId: string, payload: unknown): void {
  const view = adaptTaskDistance(payload, taskId);
  if (view) noteTaskDistances(site, [view]);
}

export function getTaskDistances(site: string): TaskDistanceMap {
  return _bySite.get(site) ?? EMPTY;
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** `site`'s task distances, re-rendering only when one changes. */
export function useTaskDistances(site: string): TaskDistanceMap {
  return useSyncExternalStore(
    subscribe,
    () => getTaskDistances(site),
    () => getTaskDistances(site),
  );
}

/** Tests only. */
export function _resetTaskDistancesForTests(): void {
  _bySite.clear();
}
